/**
 * DeviceSelector — pick the host audio source (mic device or tab/screen audio).
 *
 * On mount it requests microphone permission (so device labels populate) and
 * lists inputs. Denial is surfaced with a clear message rather than an empty
 * dropdown. Emits the chosen `CaptureSource` upward via `onChange`.
 */

import { useEffect, useState, type ReactElement } from 'react';
import {
  ensureMicrophonePermission,
  listAudioInputDevices,
  supportsDisplayMediaAudio,
  PermissionDeniedError,
  type AudioInputDevice,
  type CaptureSource,
  type CaptureSourceKind,
} from '@/features/capture/audioDevices';

interface DeviceSelectorProps {
  /** Disable interaction while capture is running. */
  disabled?: boolean;
  onChange: (source: CaptureSource) => void;
}

export function DeviceSelector({
  disabled = false,
  onChange,
}: DeviceSelectorProps): ReactElement {
  const [kind, setKind] = useState<CaptureSourceKind>('mic');
  const [devices, setDevices] = useState<AudioInputDevice[]>([]);
  const [deviceId, setDeviceId] = useState<string | null>(null);
  const [permissionError, setPermissionError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const tabSupported = supportsDisplayMediaAudio();

  useEffect(() => {
    let cancelled = false;
    async function init(): Promise<void> {
      setLoading(true);
      setPermissionError(null);
      try {
        await ensureMicrophonePermission();
        const list = await listAudioInputDevices();
        if (cancelled) return;
        setDevices(list);
        setDeviceId(list[0]?.deviceId ?? null);
      } catch (err) {
        if (cancelled) return;
        setPermissionError(
          err instanceof PermissionDeniedError
            ? err.message
            : 'Não foi possível acessar os dispositivos de áudio.',
        );
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    void init();
    return () => {
      cancelled = true;
    };
  }, []);

  // Propagate selection upward whenever it changes.
  useEffect(() => {
    onChange(kind === 'tab' ? { kind: 'tab' } : { kind: 'mic', deviceId });
  }, [kind, deviceId, onChange]);

  return (
    <div style={styles.wrap}>
      <label style={styles.field}>
        <span style={styles.label}>Fonte de áudio</span>
        <select
          value={kind}
          disabled={disabled}
          onChange={(e) => setKind(e.target.value as CaptureSourceKind)}
          style={styles.input}
        >
          <option value="mic">Microfone</option>
          <option value="tab" disabled={!tabSupported}>
            Aba / tela (áudio) {tabSupported ? '' : '— não suportado'}
          </option>
        </select>
      </label>

      {kind === 'mic' && (
        <label style={styles.field}>
          <span style={styles.label}>Microfone</span>
          <select
            value={deviceId ?? ''}
            disabled={disabled || loading || devices.length === 0}
            onChange={(e) => setDeviceId(e.target.value || null)}
            style={styles.input}
          >
            {devices.length === 0 && <option value="">Nenhum dispositivo</option>}
            {devices.map((d) => (
              <option key={d.deviceId} value={d.deviceId}>
                {d.label}
              </option>
            ))}
          </select>
        </label>
      )}

      {kind === 'tab' && (
        <p style={styles.hint}>
          Ao iniciar, escolha a aba/janela e marque “compartilhar áudio”. Funciona
          melhor no Chrome/Edge.
        </p>
      )}

      {loading && <p style={styles.muted}>Verificando permissão de áudio…</p>}
      {permissionError && <p style={styles.error}>{permissionError}</p>}
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  wrap: { display: 'flex', flexDirection: 'column', gap: '0.75rem' },
  field: { display: 'flex', flexDirection: 'column', gap: '0.35rem' },
  label: { fontSize: '0.8rem', color: 'var(--rt-text-muted)' },
  input: {
    padding: '0.6rem',
    borderRadius: 8,
    border: '1px solid var(--rt-border)',
    background: 'var(--rt-bg)',
    color: 'var(--rt-text)',
    fontSize: '0.9rem',
  },
  hint: { margin: 0, fontSize: '0.8rem', color: 'var(--rt-text-muted)' },
  muted: { margin: 0, fontSize: '0.85rem', color: 'var(--rt-text-muted)' },
  error: { margin: 0, fontSize: '0.85rem', color: 'var(--rt-danger)' },
};
